import UnlockingScript from '../script/UnlockingScript.js';
import LockingScript from '../script/LockingScript.js';
import { Writer, toHex, toArray, ReaderUint8Array, toUint8Array, WriterUint8Array } from '../primitives/utils.js';
import { hash256 } from '../primitives/Hash.js';
import LivePolicy from './fee-models/LivePolicy.js';
import Spend from '../script/Spend.js';
import { defaultBroadcaster } from './broadcasters/DefaultBroadcaster.js';
import { defaultChainTracker } from './chaintrackers/DefaultChainTracker.js';
import { Beef, BEEF_V1 } from './Beef.js';
import P2PKH from '../script/templates/P2PKH.js';
import TransactionSignature from '../primitives/TransactionSignature.js';
import Random from '../primitives/Random.js';
import { scriptVerificationBackend } from './ScriptVerificationBackend.js';
/** Post-Chronicle height used when an input's source UTXO mined-height is unobtainable. */
const POST_CHRONICLE_HEIGHT_FALLBACK = 943816;
/**
 * Represents a complete Bitcoin transaction. This class encapsulates all the details
 * required for creating, signing, and processing a Bitcoin transaction, including
 * inputs, outputs, and various transaction-related methods.
 *
 * @class Transaction
 * @property {number} version - The version number of the transaction. Used to specify
 *           which set of rules this transaction follows.
 * @property {TransactionInput[]} inputs - An array of TransactionInput objects, representing
 *           the inputs for the transaction. Each input references a previous transaction's output.
 * @property {TransactionOutput[]} outputs - An array of TransactionOutput objects, representing
 *           the outputs for the transaction. Each output specifies the amount of satoshis to be
 *           transferred and the conditions under which they can be spent.
 * @property {number} lockTime - The lock time of the transaction. If non-zero, it specifies the
 *           earliest time or block height at which the transaction can be added to the block chain.
 * @property {Record<string, any>} metadata - A key-value store for attaching additional data to
 *           the transaction object, not included in the transaction itself. Useful for adding descriptions, internal reference numbers, or other information.
 * @property {MerkleProof} [merklePath] - Optional. A merkle proof demonstrating the transaction's
 *           inclusion in a block. Useful for transaction verification using SPV.
 *
 * @example
 * // Creating a new transaction
 * let tx = new Transaction();
 * tx.addInput(...);
 * tx.addOutput(...);
 * await tx.fee();
 * await tx.sign();
 * await tx.broadcast();
 *
 * @description
 * The Transaction class provides comprehensive
 * functionality to handle various aspects of transaction creation, including
 * adding inputs and outputs, computing fees, signing the transaction, and
 * generating its binary or hexadecimal representation.
 */
export default class Transaction {
    version;
    inputs;
    outputs;
    lockTime;
    metadata;
    merklePath;
    cachedHash;
    cachedIdHex;
    rawBytesCache;
    efBytesCache;
    hexCache;
    activeSignatureHashCache;
    rawCacheState;
    /**
     * Returns the transaction-wide signature hash cache active during signing.
     * Callers outside a signing operation receive an isolated cache.
     *
     * @internal
     */
    getSignatureHashCache() {
        return this.activeSignatureHashCache ?? { hashOutputsSingle: new Map() };
    }
    completeSourceTransaction(tx, visiting, complete) {
        for (const input of tx.inputs) {
            if (input.sourceTXID == null && input.sourceTransaction != null) {
                input.sourceTXID = input.sourceTransaction.id('hex');
            }
        }
        visiting.delete(tx);
        complete.add(tx);
    }
    scheduleSourceTransactions(tx, visiting, complete, stack) {
        if (visiting.has(tx)) {
            throw new Error('Cyclic source transaction graph');
        }
        visiting.add(tx);
        stack.push({ tx, expanded: true });
        for (let i = tx.inputs.length - 1; i >= 0; i--) {
            const source = tx.inputs[i].sourceTransaction;
            if (tx.inputs[i].sourceTXID == null && source != null && !complete.has(source)) {
                stack.push({ tx: source, expanded: false });
            }
        }
    }
    /**
     * Iteratively materializes source transaction IDs so deep spend chains do not
     * recurse through `hash()` while serializing their parents.
     */
    materializeSourceTXIDs() {
        const complete = new Set();
        const visiting = new Set();
        const stack = [{ tx: this, expanded: false }];
        while (stack.length > 0) {
            const frame = stack.pop();
            if (frame == null)
                continue;
            if (complete.has(frame.tx))
                continue;
            if (frame.expanded) {
                this.completeSourceTransaction(frame.tx, visiting, complete);
                continue;
            }
            this.scheduleSourceTransactions(frame.tx, visiting, complete, stack);
        }
    }
    /**
     * Creates a new transaction, linked to its inputs and their associated merkle paths, from a BEEF V1, V2 or Atomic.
     * Optionally, you can provide a specific TXID to retrieve a particular transaction from the BEEF data.
     * If the TXID is provided but not found in the BEEF data, an error will be thrown.
     * If no TXID is provided, the last transaction in the BEEF data is returned, or the atomic txid.
     * @param beef A binary representation of transactions in BEEF format.
     * @param txid Optional TXID of the transaction to retrieve from the BEEF data.
     * @returns An anchored transaction, linked to its associated inputs populated with merkle paths.
     */
    static fromBEEF(beef, txid) {
        const { tx } = Transaction.fromAnyBeef(beef, txid);
        return tx;
    }
    /**
     * Zero-copy variant of {@link fromBEEF}. The caller must not mutate `beef`.
     */
    static fromBEEFView(beef, txid) {
        const { tx } = Transaction.fromAnyBeef(beef, txid, true);
        return tx;
    }
    /**
     * Creates a new transaction from an Atomic BEEF (BRC-95) structure.
     * Extracts the subject transaction and supporting merkle path and source transactions contained in the BEEF data
     *
     * @param beef A binary representation of an Atomic BEEF structure.
     * @returns The subject transaction, linked to its associated inputs populated with merkle paths.
     */
    static fromAtomicBEEF(beef) {
        const { tx, txid, beef: b } = Transaction.fromAnyBeef(beef);
        if (txid !== b.atomicTxid) {
            if (b.atomicTxid == null) {
                throw new Error('beef must conform to BRC-95 and must contain the subject txid.');
            }
            else {
                throw new Error(`Transaction with TXID ${b.atomicTxid} not found in BEEF data.`);
            }
        }
        if (!b.isAtomic(txid))
            throw new Error('Atomic BEEF contains unrelated transaction data.');
        return tx;
    }
    /**
     * Zero-copy variant of {@link fromAtomicBEEF}. The caller must not mutate
     * `beef` while any linked transaction remains in use.
     */
    static fromAtomicBEEFView(beef) {
        const { tx, txid, beef: b } = Transaction.fromAnyBeef(beef, undefined, true);
        if (txid !== b.atomicTxid) {
            if (b.atomicTxid == null)
                throw new Error('beef must conform to BRC-95 and must contain the subject txid.');
            throw new Error(`Transaction with TXID ${b.atomicTxid} not found in BEEF data.`);
        }
        if (!b.isAtomic(txid))
            throw new Error('Atomic BEEF contains unrelated transaction data.');
        return tx;
    }
    static fromAnyBeef(beef, txid, zeroCopy = false) {
        const b = zeroCopy && beef instanceof Uint8Array ? Beef.fromBinaryView(beef) : Beef.fromBinary(beef);
        if (b.txs.length < 1) {
            throw new Error('beef must include at least one transaction.');
        }
        const lastTx = b.txs.at(-1);
        if (lastTx == null) {
            throw new Error('beef must include at least one transaction.');
        }
        const target = txid ?? b.atomicTxid ?? lastTx.txid;
        const tx = b.findAtomicTransaction(target);
        if (tx == null) {
            if (txid == null) {
                throw new Error('beef does not contain transaction for atomic txid.');
            }
            else {
                throw new Error(`Transaction with TXID ${String(target)} not found in BEEF data.`);
            }
        }
        return { tx, beef: b, txid: target };
    }
    /**
     * Creates a new transaction, linked to its inputs and their associated merkle paths, from a EF (BRC-30) structure.
     * @param ef A binary representation of a transaction in EF format.
     * @returns An extended transaction, linked to its associated inputs by locking script and satoshis amounts only.
     */
    static fromEF(ef) {
        const br = ReaderUint8Array.makeReader(ef);
        const version = br.readUInt32LE();
        if (toHex(br.read(6)) !== '0000000000ef') {
            throw new Error('Invalid EF marker');
        }
        const inputsLength = br.readVarIntNum();
        const inputs = [];
        for (let i = 0; i < inputsLength; i++) {
            const sourceTXID = toHex(br.readReverse(32));
            const sourceOutputIndex = br.readUInt32LE();
            const scriptLength = br.readVarIntNum();
            const scriptBin = br.read(scriptLength);
            const unlockingScript = UnlockingScript.fromBinary(scriptBin);
            const sequence = br.readUInt32LE();
            const satoshis = br.readUInt64LEBn().toNumber();
            const lockingScriptLength = br.readVarIntNum();
            const lockingScriptBin = br.read(lockingScriptLength);
            const lockingScript = LockingScript.fromBinary(lockingScriptBin);
            const sourceTransaction = new Transaction(undefined, [], [], undefined);
            sourceTransaction.outputs = Array.from({ length: sourceOutputIndex + 1 }).fill(null);
            sourceTransaction.outputs[sourceOutputIndex] = {
                satoshis,
                lockingScript
            };
            inputs.push({
                sourceTransaction,
                sourceTXID,
                sourceOutputIndex,
                unlockingScript,
                sequence
            });
        }
        const outputsLength = br.readVarIntNum();
        const outputs = [];
        for (let i = 0; i < outputsLength; i++) {
            const satoshis = br.readUInt64LEBn().toNumber();
            const scriptLength = br.readVarIntNum();
            const scriptBin = br.read(scriptLength);
            const lockingScript = LockingScript.fromBinary(scriptBin);
            outputs.push({
                satoshis,
                lockingScript
            });
        }
        const lockTime = br.readUInt32LE();
        return new Transaction(version, inputs, outputs, lockTime);
    }
    /**
     * Since the validation of blockchain data is atomically transaction data validation,
     * any application seeking to validate data in output scripts must store the entire transaction as well.
     * Since the transaction data includes the output script data, saving a second copy of potentially
     * large scripts can bloat application storage requirements.
     *
     * This function efficiently parses binary transaction data to determine the offsets and lengths of each script.
     * This supports the efficient retreival of script data from transaction data.
     *
     * @param bin binary transaction data
     * @returns {
     *   inputs: { vin: number, offset: number, length: number }[]
     *   outputs: { vout: number, offset: number, length: number }[]
     * }
     */
    static parseScriptOffsets(bin) {
        const br = ReaderUint8Array.makeReader(bin);
        const inputs = [];
        const outputs = [];
        br.pos += 4; // version
        const inputsLength = br.readVarIntNum();
        for (let i = 0; i < inputsLength; i++) {
            br.pos += 36; // txid and vout
            const scriptLength = br.readVarIntNum();
            inputs.push({ vin: i, offset: br.pos, length: scriptLength });
            br.pos += scriptLength + 4; // script and sequence
        }
        const outputsLength = br.readVarIntNum();
        for (let i = 0; i < outputsLength; i++) {
            br.pos += 8; // satoshis
            const scriptLength = br.readVarIntNum();
            outputs.push({ vout: i, offset: br.pos, length: scriptLength });
            br.pos += scriptLength;
        }
        return { inputs, outputs };
    }
    static fromReader(br) {
        return Transaction.fromReaderInternal(br, false);
    }
    static fromReaderInternal(br, zeroCopyScripts) {
        const version = br.readUInt32LE();
        const inputsLength = br.readVarIntNum();
        const inputs = [];
        for (let i = 0; i < inputsLength; i++) {
            const sourceTXID = toHex(br.readReverse(32));
            const sourceOutputIndex = br.readUInt32LE();
            const scriptLength = br.readVarIntNum();
            const scriptBin = zeroCopyScripts && br instanceof ReaderUint8Array
                ? br.readView(scriptLength)
                : br.read(scriptLength);
            const unlockingScript = zeroCopyScripts && scriptBin instanceof Uint8Array
                ? UnlockingScript.fromBinaryView(scriptBin)
                : UnlockingScript.fromBinary(scriptBin);
            const sequence = br.readUInt32LE();
            inputs.push({
                sourceTXID,
                sourceOutputIndex,
                unlockingScript,
                sequence
            });
        }
        const outputsLength = br.readVarIntNum();
        const outputs = [];
        for (let i = 0; i < outputsLength; i++) {
            const satoshis = br.readUInt64LEBn().toNumber();
            const scriptLength = br.readVarIntNum();
            const scriptBin = zeroCopyScripts && br instanceof ReaderUint8Array
                ? br.readView(scriptLength)
                : br.read(scriptLength);
            const lockingScript = zeroCopyScripts && scriptBin instanceof Uint8Array
                ? LockingScript.fromBinaryView(scriptBin)
                : LockingScript.fromBinary(scriptBin);
            outputs.push({
                satoshis,
                lockingScript
            });
        }
        const lockTime = br.readUInt32LE();
        return new Transaction(version, inputs, outputs, lockTime);
    }
    /**
     * Creates a Transaction instance from a binary array.
     *
     * @static
     * @param {number[]} bin - The binary array representation of the transaction.
     * @returns {Transaction} - A new Transaction instance.
     */
    static fromBinary(bin) {
        const rawBytes = Uint8Array.from(bin);
        const br = new ReaderUint8Array(rawBytes);
        const tx = Transaction.fromReaderInternal(br, true);
        tx.rawBytesCache = rawBytes;
        tx.captureSerializationState();
        return tx;
    }
    /**
     * Parses a transaction while retaining zero-copy views over `bin` for the raw
     * transaction and its scripts. The caller must not mutate `bin`.
     */
    static fromBinaryView(bin) {
        const br = new ReaderUint8Array(bin);
        const tx = Transaction.fromReaderInternal(br, true);
        if (!br.eof())
            throw new Error('Serialized transaction contains trailing data');
        tx.rawBytesCache = bin;
        tx.captureSerializationState();
        return tx;
    }
    /**
     * Creates a Transaction instance from a hexadecimal string.
     *
     * @static
     * @param {string} hex - The hexadecimal string representation of the transaction.
     * @returns {Transaction} - A new Transaction instance.
     */
    static fromHex(hex) {
        const rawBytes = toUint8Array(hex, 'hex');
        const br = new ReaderUint8Array(rawBytes);
        const tx = Transaction.fromReaderInternal(br, true);
        tx.rawBytesCache = rawBytes;
        tx.hexCache = toHex(rawBytes);
        tx.captureSerializationState();
        return tx;
    }
    /**
     * Creates a Transaction instance from a hexadecimal string encoded EF.
     *
     * @static
     * @param {string} hex - The hexadecimal string representation of the transaction EF.
     * @returns {Transaction} - A new Transaction instance.
     */
    static fromHexEF(hex) {
        return Transaction.fromEF(toUint8Array(hex, 'hex'));
    }
    /**
     * Creates a Transaction instance from a hexadecimal string encoded BEEF.
     * Optionally, you can provide a specific TXID to retrieve a particular transaction from the BEEF data.
     * If the TXID is provided but not found in the BEEF data, an error will be thrown.
     * If no TXID is provided, the last transaction in the BEEF data is returned.
     *
     * @static
     * @param {string} hex - The hexadecimal string representation of the transaction BEEF.
     * @param {string} [txid] - Optional TXID of the transaction to retrieve from the BEEF data.
     * @returns {Transaction} - A new Transaction instance.
     */
    static fromHexBEEF(hex, txid) {
        return Transaction.fromBEEF(toArray(hex, 'hex'), txid);
    }
    constructor(version = 1, inputs = [], outputs = [], lockTime = 0, metadata = new Map(), merklePath) {
        this.version = version;
        this.inputs = inputs;
        this.outputs = outputs;
        this.lockTime = lockTime;
        this.metadata = metadata;
        this.merklePath = merklePath;
    }
    invalidateSerializationCaches() {
        this.cachedHash = undefined;
        this.cachedIdHex = undefined;
        this.rawBytesCache = undefined;
        this.efBytesCache = undefined;
        this.hexCache = undefined;
        this.rawCacheState = undefined;
    }
    sourceTransactionId(input) {
        return input.sourceTXID == null ? input.sourceTransaction?.id('hex') : undefined;
    }
    captureSerializationState() {
        this.rawCacheState = {
            version: this.version,
            lockTime: this.lockTime,
            inputs: this.inputs.map(ref => {
                const sourceOutput = ref.sourceTransaction?.outputs[ref.sourceOutputIndex];
                return {
                    ref,
                    sourceTXID: ref.sourceTXID,
                    sourceTransactionId: this.sourceTransactionId(ref),
                    sourceOutputIndex: ref.sourceOutputIndex,
                    sequence: ref.sequence,
                    unlockingScript: ref.unlockingScript,
                    unlockingScriptBytes: ref.unlockingScript?.toUint8Array(),
                    sourceOutput,
                    sourceSatoshis: sourceOutput?.satoshis,
                    sourceLockingScript: sourceOutput?.lockingScript,
                    sourceLockingScriptBytes: sourceOutput?.lockingScript.toUint8Array()
                };
            }),
            outputs: this.outputs.map(ref => ({
                ref,
                satoshis: ref.satoshis,
                lockingScript: ref.lockingScript,
                lockingScriptBytes: ref.lockingScript.toUint8Array()
            }))
        };
    }
    serializationCacheMatchesState() {
        const cached = this.rawCacheState;
        if (cached?.version !== this.version ||
            cached.lockTime !== this.lockTime ||
            cached.inputs.length !== this.inputs.length ||
            cached.outputs.length !== this.outputs.length)
            return false;
        for (let i = 0; i < this.inputs.length; i++) {
            const input = this.inputs[i];
            const state = cached.inputs[i];
            const sourceOutput = input.sourceTransaction?.outputs[input.sourceOutputIndex];
            if (state.ref !== input ||
                state.sourceTXID !== input.sourceTXID ||
                state.sourceTransactionId !== this.sourceTransactionId(input) ||
                state.sourceOutputIndex !== input.sourceOutputIndex ||
                state.sequence !== input.sequence ||
                state.unlockingScript !== input.unlockingScript ||
                state.unlockingScriptBytes !== input.unlockingScript?.toUint8Array() ||
                state.sourceOutput !== sourceOutput ||
                state.sourceSatoshis !== sourceOutput?.satoshis ||
                state.sourceLockingScript !== sourceOutput?.lockingScript ||
                state.sourceLockingScriptBytes !== sourceOutput?.lockingScript.toUint8Array())
                return false;
        }
        for (let i = 0; i < this.outputs.length; i++) {
            const output = this.outputs[i];
            const state = cached.outputs[i];
            if (state.ref !== output ||
                state.satoshis !== output.satoshis ||
                state.lockingScript !== output.lockingScript ||
                state.lockingScriptBytes !== output.lockingScript.toUint8Array())
                return false;
        }
        return true;
    }
    /**
     * Adds a new input to the transaction.
     *
     * @param {TransactionInput} input - The TransactionInput object to add to the transaction.
     * @throws {Error} - If the input does not have a sourceTXID or sourceTransaction defined.
     */
    addInput(input) {
        if (input.sourceTXID === undefined && input.sourceTransaction === undefined) {
            throw new TypeError('A reference to an an input transaction is required. If the input transaction itself cannot be referenced, its TXID must still be provided.');
        }
        // If the input sequence number hasn't been set, the expectation is that it is final.
        input.sequence ??= 0xffffffff;
        this.invalidateSerializationCaches();
        this.inputs.push(input);
    }
    /**
     * Adds a new output to the transaction.
     *
     * @param {TransactionOutput} output - The TransactionOutput object to add to the transaction.
     */
    addOutput(output) {
        this.invalidateSerializationCaches();
        if (output.change !== true) {
            if (output.satoshis === undefined) {
                throw new TypeError('either satoshis must be defined or change must be set to true');
            }
            if (output.satoshis < 0) {
                throw new Error('satoshis must be a positive integer or zero');
            }
        }
        if (output.lockingScript == null)
            throw new Error('lockingScript must be defined');
        this.outputs.push(output);
    }
    /**
     * Adds a new P2PKH output to the transaction.
     *
     * @param {number[] | string} address - The P2PKH address of the output.
     * @param {number} [satoshis] - The number of satoshis to send to the address - if not provided, the output is considered a change output.
     *
     */
    addP2PKHOutput(address, satoshis) {
        const lockingScript = new P2PKH().lock(address);
        if (satoshis === undefined) {
            return this.addOutput({ lockingScript, change: true });
        }
        this.addOutput({
            lockingScript,
            satoshis
        });
    }
    /**
     * Updates the transaction's metadata.
     *
     * @param {Record<string, any>} metadata - The metadata object to merge into the existing metadata.
     */
    updateMetadata(metadata) {
        this.metadata = {
            ...this.metadata,
            ...metadata
        };
    }
    /**
     * Computes fees prior to signing.
     * If no fee model is provided, uses a LivePolicy fee model that fetches current rates from ARC.
     * If fee is a number, the transaction uses that value as fee.
     *
     * @param modelOrFee - The initialized fee model to use or fixed fee for the transaction
     * @param changeDistribution - Specifies how the change should be distributed
     * amongst the change outputs
     *
     */
    async fee(modelOrFee = LivePolicy.getInstance(), changeDistribution = 'equal') {
        this.invalidateSerializationCaches();
        if (typeof modelOrFee === 'number') {
            const sats = modelOrFee;
            modelOrFee = {
                computeFee: async () => sats
            };
        }
        const fee = await modelOrFee.computeFee(this);
        const change = this.calculateChange(fee);
        if (change <= 0) {
            this.outputs = this.outputs.filter(output => output.change !== true);
            return;
        }
        this.distributeChange(change, changeDistribution);
    }
    calculateChange(fee) {
        let change = 0;
        for (const input of this.inputs) {
            if (typeof input.sourceTransaction !== 'object') {
                throw new TypeError('Source transactions are required for all inputs during fee computation');
            }
            change += input.sourceTransaction.outputs[input.sourceOutputIndex].satoshis ?? 0;
        }
        change -= fee;
        for (const out of this.outputs) {
            if (out.change !== true) {
                if (out.satoshis !== undefined) {
                    change -= out.satoshis;
                }
            }
        }
        return change;
    }
    distributeChange(change, changeDistribution) {
        let distributedChange = 0;
        const changeOutputs = this.outputs.filter(out => out.change);
        if (changeDistribution === 'random') {
            distributedChange = this.distributeRandomChange(change, changeOutputs);
        }
        else if (changeDistribution === 'equal') {
            distributedChange = this.distributeEqualChange(change, changeOutputs);
        }
        if (distributedChange < change) {
            const lastOutput = this.outputs.at(-1);
            if (lastOutput.satoshis === undefined) {
                lastOutput.satoshis = change - distributedChange;
            }
            else {
                lastOutput.satoshis += change - distributedChange;
            }
        }
    }
    distributeRandomChange(change, changeOutputs) {
        let distributedChange = 0;
        let changeToUse = change;
        const benfordNumbers = Array.from({ length: changeOutputs.length }).fill(1);
        changeToUse -= changeOutputs.length;
        distributedChange += changeOutputs.length;
        for (let i = 0; i < changeOutputs.length - 1; i++) {
            const portion = this.benfordNumber(0, changeToUse);
            benfordNumbers[i] = benfordNumbers[i] + portion;
            distributedChange += portion;
            changeToUse -= portion;
        }
        for (const output of this.outputs) {
            if (output.change === true)
                output.satoshis = benfordNumbers.shift();
        }
        return distributedChange;
    }
    distributeEqualChange(change, changeOutputs) {
        let distributedChange = 0;
        const perOutput = Math.floor(change / changeOutputs.length);
        for (const out of changeOutputs) {
            distributedChange += perOutput;
            out.satoshis = perOutput;
        }
        return distributedChange;
    }
    benfordNumber(min, max) {
        const d = (Random(1)[0] % 9) + 1;
        return Math.floor(min + ((max - min) * Math.log10(1 + 1 / d)) / Math.log10(10));
    }
    /**
     * Utility method that returns the current fee based on inputs and outputs
     *
     * @returns The current transaction fee
     */
    getFee() {
        let totalIn = 0;
        for (const input of this.inputs) {
            if (typeof input.sourceTransaction !== 'object') {
                throw new TypeError('Source transactions or sourceSatoshis are required for all inputs to calculate fee');
            }
            totalIn += input.sourceTransaction.outputs[input.sourceOutputIndex].satoshis ?? 0;
        }
        let totalOut = 0;
        for (const output of this.outputs) {
            totalOut += output.satoshis ?? 0;
        }
        return totalIn - totalOut;
    }
    /**
     * Signs a transaction, hydrating all its unlocking scripts based on the provided script templates where they are available.
     * @param options - Signing behavior. Set `skipExistingSignatures` to preserve inputs that already have an unlocking script.
     */
    async sign(options = {}) {
        this.invalidateSerializationCaches();
        for (const out of this.outputs) {
            if (out.satoshis === undefined) {
                if (out.change === true) {
                    throw new Error('There are still change outputs with uncomputed amounts. Use the fee() method to compute the change amounts and transaction fees prior to signing.');
                }
                else {
                    throw new Error('One or more transaction outputs is missing an amount. Ensure all output amounts are provided before signing.');
                }
            }
        }
        this.materializeSourceTXIDs();
        const previousCache = this.activeSignatureHashCache;
        this.activeSignatureHashCache = { hashOutputsSingle: new Map() };
        let unlockingScripts;
        try {
            unlockingScripts = await Promise.all(this.inputs.map(async (x, i) => {
                if (options.skipExistingSignatures === true && this.inputs[i].unlockingScript != null) {
                    return this.inputs[i].unlockingScript;
                }
                if (typeof this.inputs[i].unlockingScriptTemplate === 'object') {
                    return await this.inputs[i]?.unlockingScriptTemplate?.sign(this, i);
                }
                else {
                    return await Promise.resolve(undefined);
                }
            }));
        }
        finally {
            this.activeSignatureHashCache = previousCache;
        }
        for (let i = 0, l = this.inputs.length; i < l; i++) {
            if (typeof this.inputs[i].unlockingScriptTemplate === 'object') {
                this.inputs[i].unlockingScript = unlockingScripts[i];
            }
        }
        // A custom template may serialize the transaction while signing. Ensure
        // bytes cached during template execution cannot survive script hydration.
        this.invalidateSerializationCaches();
    }
    /**
     * Broadcasts a transaction.
     *
     * @param broadcaster The Broadcaster instance wwhere the transaction will be sent
     * @returns A BroadcastResponse or BroadcastFailure from the Broadcaster
     */
    async broadcast(broadcaster = defaultBroadcaster()) {
        return await broadcaster.broadcast(this);
    }
    writeTransactionBody(writer) {
        writer.writeUInt32LE(this.version);
        writer.writeVarIntNum(this.inputs.length);
        for (const i of this.inputs) {
            if (i.sourceTXID === undefined) {
                if (i.sourceTransaction == null) {
                    throw new Error('sourceTransaction is undefined');
                }
                else {
                    writer.write(i.sourceTransaction.hash());
                }
            }
            else {
                writer.writeReverse(toArray(i.sourceTXID, 'hex'));
            }
            writer.writeUInt32LE(i.sourceOutputIndex);
            if (i.unlockingScript == null) {
                throw new Error('unlockingScript is undefined');
            }
            const scriptBin = i.unlockingScript.toUint8Array();
            writer.writeVarIntNum(scriptBin.length);
            writer.write(scriptBin);
            writer.writeUInt32LE(i.sequence ?? 0xffffffff);
        }
        writer.writeVarIntNum(this.outputs.length);
        for (const o of this.outputs) {
            writer.writeUInt64LE(o.satoshis ?? 0);
            const scriptBin = o.lockingScript.toUint8Array();
            writer.writeVarIntNum(scriptBin.length);
            writer.write(scriptBin);
        }
        writer.writeUInt32LE(this.lockTime);
    }
    buildSerializedBytes() {
        const writer = new WriterUint8Array();
        this.writeTransactionBody(writer);
        return writer.toUint8Array();
    }
    getSerializedBytes() {
        if (this.rawBytesCache == null || !this.serializationCacheMatchesState()) {
            this.invalidateSerializationCaches();
            this.rawBytesCache = this.buildSerializedBytes();
            this.captureSerializationState();
        }
        return this.rawBytesCache;
    }
    /**
     * Converts the transaction to a binary array format.
     *
     * @returns {number[]} - The binary array representation of the transaction.
     */
    toBinary() {
        return Array.from(this.getSerializedBytes());
    }
    toUint8Array() {
        return this.getSerializedBytes();
    }
    writeEF(writer) {
        writer.writeUInt32LE(this.version);
        writer.write([0, 0, 0, 0, 0, 0xef]);
        writer.writeVarIntNum(this.inputs.length);
        for (const i of this.inputs) {
            if (i.sourceTransaction === undefined) {
                throw new TypeError('All inputs must have source transactions when serializing to EF format');
            }
            if (i.sourceTXID === undefined) {
                writer.write(i.sourceTransaction.hash());
            }
            else {
                writer.write(toArray(i.sourceTXID, 'hex').reverse());
            }
            writer.writeUInt32LE(i.sourceOutputIndex);
            if (i.unlockingScript == null) {
                throw new Error('unlockingScript is undefined');
            }
            const scriptBin = i.unlockingScript.toUint8Array();
            writer.writeVarIntNum(scriptBin.length);
            writer.write(scriptBin);
            writer.writeUInt32LE(i.sequence ?? 0xffffffff); // default to max sequence
            writer.writeUInt64LE(i.sourceTransaction.outputs[i.sourceOutputIndex].satoshis ?? 0);
            const lockingScriptBin = i.sourceTransaction.outputs[i.sourceOutputIndex].lockingScript.toUint8Array();
            writer.writeVarIntNum(lockingScriptBin.length);
            writer.write(lockingScriptBin);
        }
        writer.writeVarIntNum(this.outputs.length);
        for (const o of this.outputs) {
            writer.writeUInt64LE(o.satoshis ?? 0);
            const scriptBin = o.lockingScript.toUint8Array();
            writer.writeVarIntNum(scriptBin.length);
            writer.write(scriptBin);
        }
        writer.writeUInt32LE(this.lockTime);
    }
    /**
     * Converts the transaction to a BRC-30 EF format.
     *
     * @returns {number[]} - The BRC-30 EF representation of the transaction.
     */
    toEF() {
        return Array.from(this.getEFBytes());
    }
    /**
     * Converts the transaction to a BRC-30 EF format.
     *
     * @remarks This is an alias for {@link toEFBinary}. The returned view is
     * memoized for verifier hot paths and must be treated as immutable.
     *
     * @returns {Uint8Array} - The BRC-30 EF representation of the transaction.
     */
    toEFUint8Array() {
        return this.toEFBinary();
    }
    getEFBytes() {
        if (this.efBytesCache == null || !this.serializationCacheMatchesState()) {
            this.invalidateSerializationCaches();
            const writer = new WriterUint8Array();
            this.writeEF(writer);
            this.efBytesCache = writer.toUint8Array();
            this.captureSerializationState();
        }
        return this.efBytesCache;
    }
    /**
     * Converts the transaction to a memoized BRC-30 EF byte array.
     *
     * @remarks The returned view is reused until transaction or referenced
     * source-output serialization state changes. Treat it as immutable; call
     * `.slice()` when an independently mutable copy is required.
     *
     * @returns {Uint8Array} The cached BRC-30 EF representation.
     */
    toEFBinary() {
        return this.getEFBytes();
    }
    /**
     * Converts the transaction to a hexadecimal string EF.
     *
     * @returns {string} - The hexadecimal string representation of the transaction EF.
     */
    toHexEF() {
        return toHex(this.toEFBinary());
    }
    /**
     * Converts the transaction to a hexadecimal string format.
     *
     * @returns {string} - The hexadecimal string representation of the transaction.
     */
    toHex() {
        const bytes = this.getSerializedBytes();
        if (this.hexCache != null)
            return this.hexCache;
        const hex = toHex(bytes);
        this.hexCache = hex;
        return hex;
    }
    /**
     * Converts the transaction to a hexadecimal string BEEF.
     *
     * @returns {string} - The hexadecimal string representation of the transaction BEEF.
     */
    toHexBEEF() {
        return toHex(this.toBEEF());
    }
    /**
     * Converts the transaction to a hexadecimal string Atomic BEEF.
     *
     * @returns {string} - The hexadecimal string representation of the transaction Atomic BEEF.
     */
    toHexAtomicBEEF() {
        return toHex(this.toAtomicBEEF());
    }
    /**
     * Calculates the transaction's hash.
     *
     * @param {'hex' | undefined} enc - The encoding to use for the hash. If 'hex', returns a hexadecimal string; otherwise returns a binary array.
     * @returns {string | number[]} - The hash of the transaction in the specified format.
     */
    hash(enc) {
        const bytes = this.getSerializedBytes();
        this.cachedHash ??= hash256(bytes);
        if (enc === 'hex') {
            return toHex(this.cachedHash);
        }
        return Array.from(this.cachedHash);
    }
    /**
     * Calculates the transaction's ID.
     *
     * @param {'hex' | undefined} enc - The encoding to use for the ID. If 'hex', returns a hexadecimal string; otherwise returns a binary array.
     * @returns {string | number[]} - The ID of the transaction in the specified format.
     */
    id(enc) {
        // Validate public mutable transaction state before consulting either ID
        // cache. getSerializedBytes() clears both when any signed field changed.
        this.getSerializedBytes();
        if (enc === 'hex' && this.cachedIdHex != null)
            return this.cachedIdHex;
        const id = [...this.hash()];
        id.reverse();
        if (enc === 'hex') {
            this.cachedIdHex = toHex(id);
            return this.cachedIdHex;
        }
        return id;
    }
    async completeVerificationFromMerklePath(tx, scriptsOnly, chainTracker, getTxid, verifiedTransactions, verifiedTxids) {
        if (typeof tx.merklePath !== 'object')
            return false;
        if (scriptsOnly) {
            verifiedTransactions.add(tx);
            return true;
        }
        if (await tx.merklePath.verify(getTxid(), chainTracker)) {
            verifiedTxids.add(getTxid());
            return true;
        }
        throw new Error(`Invalid merkle path for transaction ${getTxid()}`);
    }
    async verifyTransactionFee(tx, feeModel, getTxid) {
        if (feeModel === undefined)
            return;
        if (tx === undefined)
            throw new Error('Transaction is undefined');
        const copy = Transaction.fromEF(tx.toEF());
        delete copy.outputs[0].satoshis;
        copy.outputs[0].change = true;
        await copy.fee(feeModel);
        if (tx.getFee() < copy.getFee()) {
            throw new Error(`Verification failed because the transaction ${getTxid()} has an insufficient fee and has not been mined.`);
        }
    }
    queueSourceTransactionForVerification(sourceTransaction, sourceTxid, state) {
        if (state.scriptsOnly) {
            if (!state.verifiedTransactions.has(sourceTransaction) &&
                !state.queuedTransactions.has(sourceTransaction)) {
                state.txQueue.push(sourceTransaction);
                state.queuedTransactions.add(sourceTransaction);
            }
            return;
        }
        if (!state.verifiedTxids.has(sourceTxid) &&
            !state.queuedTxids.has(sourceTxid)) {
            state.txQueue.push(sourceTransaction);
            state.queuedTxids.add(sourceTxid);
        }
    }
    verifyTransactionInputs(tx, useVerifier, getTxid, state) {
        let inputTotal = 0;
        const sigHashCache = { hashOutputsSingle: new Map() };
        for (let index = 0; index < tx.inputs.length; index++) {
            const input = tx.inputs[index];
            if (typeof input.sourceTransaction !== 'object') {
                throw new TypeError(`Verification failed because the input at index ${index} of transaction ${getTxid()} is missing an associated source transaction. This source transaction is required for transaction verification because there is no merkle proof for the transaction spending a UTXO it contains.`);
            }
            if (typeof input.unlockingScript !== 'object') {
                throw new TypeError(`Verification failed because the input at index ${index} of transaction ${getTxid()} is missing an associated unlocking script. This script is required for transaction verification because there is no merkle proof for the transaction spending the UTXO.`);
            }
            const sourceTransaction = input.sourceTransaction;
            const sourceOutput = sourceTransaction.outputs[input.sourceOutputIndex];
            inputTotal += sourceOutput.satoshis ?? 0;
            const sourceTxid = state.scriptsOnly && input.sourceTXID !== undefined
                ? input.sourceTXID
                : sourceTransaction.id('hex');
            this.queueSourceTransactionForVerification(sourceTransaction, sourceTxid, state);
            input.sourceTXID ??= sourceTxid;
            if (!useVerifier &&
                !new Spend({
                    sourceTXID: input.sourceTXID,
                    sourceOutputIndex: input.sourceOutputIndex,
                    lockingScript: sourceOutput.lockingScript,
                    sourceSatoshis: sourceOutput.satoshis ?? 0,
                    transactionVersion: tx.version,
                    otherInputs: [],
                    allInputs: tx.inputs,
                    unlockingScript: input.unlockingScript,
                    inputSequence: input.sequence ?? 0xffffffff,
                    inputIndex: index,
                    outputs: tx.outputs,
                    lockTime: tx.lockTime,
                    memoryLimit: state.memoryLimit,
                    sigHashCache
                }).validateJavaScript()) {
                return { valid: false, inputTotal };
            }
        }
        return { valid: true, inputTotal };
    }
    totalVerifiedOutputs(tx) {
        let outputTotal = 0;
        for (const output of tx.outputs) {
            if (typeof output.satoshis !== 'number') {
                throw new TypeError('Every output must have a defined amount during transaction verification.');
            }
            outputTotal += output.satoshis;
        }
        return outputTotal;
    }
    async verifyQueuedScripts(verifierQueue, selectedVerifier) {
        if (verifierQueue.length === 0 || selectedVerifier === undefined)
            return;
        const scriptVerdicts = selectedVerifier.verifyScriptsBatch === undefined
            ? await Promise.all(verifierQueue.map(async (params) => await selectedVerifier.verifyScripts(params)))
            : await selectedVerifier.verifyScriptsBatch(verifierQueue);
        if (scriptVerdicts.length !== verifierQueue.length) {
            throw new Error('Script verifier returned an invalid batch result count');
        }
        const failedIndex = scriptVerdicts.findIndex(valid => !valid);
        if (failedIndex >= 0) {
            throw new Error(`Script verification failed for transaction ${verifierQueue[failedIndex].tx.id('hex')}`);
        }
    }
    isTransactionAlreadyVerified(tx, getTxid, state) {
        return state.scriptsOnly
            ? state.verifiedTransactions.has(tx)
            : state.verifiedTxids.has(getTxid());
    }
    async verifyUnminedTransaction(tx, getTxid, context) {
        const { feeModel, memoryLimit, selectedVerifier, verifierQueue } = context;
        await this.verifyTransactionFee(tx, feeModel, getTxid);
        const verifierParams = {
            tx,
            blockHeight: POST_CHRONICLE_HEIGHT_FALLBACK,
            consensus: true,
            ...(memoryLimit === undefined ? {} : { memoryLimit })
        };
        const useVerifier = selectedVerifier !== undefined &&
            (memoryLimit === undefined ||
                selectedVerifier.supportsMemoryLimit === true) &&
            (selectedVerifier.shouldVerifyScripts?.(verifierParams) ?? true);
        const inputVerification = this.verifyTransactionInputs(tx, useVerifier, getTxid, context);
        if (!inputVerification.valid)
            return false;
        if (useVerifier)
            verifierQueue.push(verifierParams);
        if (this.totalVerifiedOutputs(tx) > inputVerification.inputTotal)
            return false;
        if (context.scriptsOnly)
            context.verifiedTransactions.add(tx);
        else
            context.verifiedTxids.add(getTxid());
        return true;
    }
    /**
     * Verifies the legitimacy of the Bitcoin transaction according to the rules of SPV by ensuring all the input transactions link back to valid block headers, the chain of spends for all inputs are valid, and the sum of inputs is not less than the sum of outputs.
     *
     * @param chainTracker - An instance of ChainTracker, a Bitcoin block header tracker. If the value is set to 'scripts only', headers will not be verified. If not provided then the default chain tracker will be used.
     * @param feeModel - An instance of FeeModel, a fee model to use for fee calculation. If not provided then the default fee model will be used.
     * @param memoryLimit - Optional caller-supplied local script-interpreter
     * memory budget. If omitted, post-Genesis validation does not impose an
     * arbitrary SDK memory cap.
     * @param verifier - An optional asynchronous script backend. Adaptive backends may decline before execution to preserve the JavaScript path.
     *
     * @returns Whether the transaction is valid according to the rules of SPV.
     *
     * @example tx.verify(new WhatsOnChain(), LivePolicy.getInstance())
     */
    async verify(chainTracker = defaultChainTracker(), feeModel, memoryLimit, verifier) {
        const scriptsOnly = chainTracker === 'scripts only';
        const selectedVerifier = verifier ?? scriptVerificationBackend();
        if (!scriptsOnly)
            this.materializeSourceTXIDs();
        const verifiedTxids = new Set();
        const verifiedTransactions = new Set();
        const txQueue = [this];
        const queuedTxids = new Set();
        if (!scriptsOnly)
            queuedTxids.add(this.id('hex'));
        const queuedTransactions = new Set(txQueue);
        const verifierQueue = [];
        const verificationContext = {
            scriptsOnly,
            memoryLimit,
            txQueue,
            queuedTransactions,
            queuedTxids,
            verifiedTransactions,
            verifiedTxids,
            feeModel,
            selectedVerifier,
            verifierQueue
        };
        let queueIndex = 0;
        while (queueIndex < txQueue.length) {
            const tx = txQueue[queueIndex++];
            let txid;
            const getTxid = () => {
                txid ??= tx.id('hex');
                return txid;
            };
            if (this.isTransactionAlreadyVerified(tx, getTxid, verificationContext)) {
                continue;
            }
            if (await this.completeVerificationFromMerklePath(tx, scriptsOnly, chainTracker, getTxid, verifiedTransactions, verifiedTxids)) {
                continue;
            }
            if (!(await this.verifyUnminedTransaction(tx, getTxid, verificationContext)))
                return false;
        }
        await this.verifyQueuedScripts(verifierQueue, selectedVerifier);
        return true;
    }
    /**
     * Serializes this transaction, together with its inputs and the respective merkle proofs, into the BEEF (BRC-62) format. This enables efficient verification of its compliance with the rules of SPV.
     *
     * @param writer The writer to serialize to
     * @param allowPartial If true, error will not be thrown if there are any missing sourceTransactions.
     *
     * @returns The serialized BEEF structure
     * @throws Error if there are any missing sourceTransactions unless `allowPartial` is true.
     */
    writeSerializedBEEF(writer, allowPartial) {
        this.materializeSourceTXIDs();
        writer.writeUInt32LE(BEEF_V1);
        const { bumps, txs } = this.collectBEEFTransactions(allowPartial);
        writer.writeVarIntNum(bumps.length);
        const bumpBytes = this.reserveBEEFWriter(writer, bumps, txs);
        for (let i = 0; i < bumps.length; i++) {
            writer.write(bumpBytes?.[i] ?? bumps[i].toBinary());
        }
        writer.writeVarIntNum(txs.length);
        for (const item of txs) {
            writer.write(item.tx.toUint8Array());
            if (typeof item.pathIndex === 'number') {
                writer.writeUInt8(1);
                writer.writeVarIntNum(item.pathIndex);
            }
            else {
                writer.writeUInt8(0);
            }
        }
    }
    collectBEEFTransactions(allowPartial) {
        const bumps = [];
        const bumpIndexByInstance = new Map();
        const bumpIndexByRoot = new Map();
        const txs = [];
        const seenTxids = new Set();
        const scheduledTxids = new Set();
        const stack = [{ tx: this, expanded: false }];
        while (stack.length > 0) {
            const frame = stack.pop();
            if (frame == null)
                continue;
            if (frame.expanded) {
                this.appendBEEFTransaction(frame.tx, seenTxids, txs, bumps, bumpIndexByInstance, bumpIndexByRoot);
                continue;
            }
            this.scheduleBEEFTransaction(frame.tx, allowPartial, scheduledTxids, stack);
        }
        return { bumps, txs };
    }
    appendBEEFTransaction(tx, seenTxids, txs, bumps, bumpIndexByInstance, bumpIndexByRoot) {
        const txid = tx.id('hex');
        if (seenTxids.has(txid))
            return;
        const item = { tx };
        if (tx.merklePath != null) {
            item.pathIndex = this.getBEEFPathIndex(tx.merklePath, bumps, bumpIndexByInstance, bumpIndexByRoot);
        }
        seenTxids.add(txid);
        txs.push(item);
    }
    scheduleBEEFTransaction(tx, allowPartial, scheduledTxids, stack) {
        const txid = tx.id('hex');
        if (scheduledTxids.has(txid))
            return;
        scheduledTxids.add(txid);
        stack.push({ tx, expanded: true });
        if (tx.merklePath != null)
            return;
        for (const input of tx.inputs) {
            const source = input.sourceTransaction;
            if (source != null)
                stack.push({ tx: source, expanded: false });
            else if (allowPartial === false)
                throw new Error('A required source transaction is missing!');
        }
    }
    getBEEFPathIndex(merklePath, bumps, bumpIndexByInstance, bumpIndexByRoot) {
        const existingByInstance = bumpIndexByInstance.get(merklePath);
        if (existingByInstance !== undefined)
            return existingByInstance;
        const key = `${merklePath.blockHeight}:${merklePath.computeRoot()}`;
        const existingByRoot = bumpIndexByRoot.get(key);
        if (existingByRoot !== undefined) {
            bumps[existingByRoot].combine(merklePath);
            bumpIndexByInstance.set(merklePath, existingByRoot);
            return existingByRoot;
        }
        const newIndex = bumps.length;
        bumps.push(merklePath);
        bumpIndexByInstance.set(merklePath, newIndex);
        bumpIndexByRoot.set(key, newIndex);
        return newIndex;
    }
    reserveBEEFWriter(writer, bumps, txs) {
        let bumpBytes;
        if (writer instanceof WriterUint8Array) {
            bumpBytes = bumps.map(bump => bump.toBinaryUint8Array());
            let remainingBytes = 16;
            for (const bytes of bumpBytes)
                remainingBytes += bytes.length;
            for (const item of txs)
                remainingBytes += item.tx.toUint8Array().length + 10;
            writer.reserve(remainingBytes);
        }
        return bumpBytes;
    }
    /**
     * Serializes this transaction, together with its inputs and the respective merkle proofs, into the BEEF (BRC-62) format. This enables efficient verification of its compliance with the rules of SPV.
     *
     * @param allowPartial If true, error will not be thrown if there are any missing sourceTransactions.
     *
     * @returns {number[]} The serialized BEEF structure
     * @throws Error if there are any missing sourceTransactions unless `allowPartial` is true.
     */
    toBEEF(allowPartial) {
        const writer = new Writer();
        this.writeSerializedBEEF(writer, allowPartial);
        return writer.toArray();
    }
    /**
     * Serializes this transaction, together with its inputs and the respective merkle proofs, into the BEEF (BRC-62) format. This enables efficient verification of its compliance with the rules of SPV.
     *
     * @param allowPartial If true, error will not be thrown if there are any missing sourceTransactions.
     *
     * @returns {number[]} The serialized BEEF structure
     * @throws Error if there are any missing sourceTransactions unless `allowPartial` is true.
     * @deprecated This historical method returns a legacy `number[]` at runtime
     * despite its declared type. Use {@link toBEEFBytes} for a real Uint8Array.
     */
    toBEEFUint8Array(allowPartial) {
        const writer = new WriterUint8Array();
        this.writeSerializedBEEF(writer, allowPartial);
        return writer.toArray();
    }
    /**
     * Serializes BEEF to a real typed byte array.
     *
     * @remarks This replaces the historical `toBEEFUint8Array` method, whose
     * runtime value is a legacy `number[]` despite its declared return type.
     */
    toBEEFBytes(allowPartial) {
        const writer = new WriterUint8Array();
        this.writeSerializedBEEF(writer, allowPartial);
        return writer.toUint8Array();
    }
    /**
     * Serializes this transaction and its inputs into the Atomic BEEF (BRC-95) format.
     * The Atomic BEEF format starts with a 4-byte prefix `0x01010101`, followed by the TXID of the subject transaction,
     * and then the BEEF data containing only the subject transaction and its dependencies.
     * This format ensures that the BEEF structure is atomic and contains no unrelated transactions.
     *
     * @param allowPartial If true, error will not be thrown if there are any missing sourceTransactions.
     *
     * @returns {number[]} - The serialized Atomic BEEF structure.
     * @throws Error if there are any missing sourceTransactions unless `allowPartial` is true.
     */
    toAtomicBEEF(allowPartial) {
        this.materializeSourceTXIDs();
        const prefix = [1, 1, 1, 1];
        const txHash = this.hash();
        const beefData = this.toBEEF(allowPartial);
        return prefix.concat(txHash, beefData);
    }
    /**
     * Serializes this transaction and its inputs into the Atomic BEEF (BRC-95) format.
     * The Atomic BEEF format starts with a 4-byte prefix `0x01010101`, followed by the TXID of the subject transaction,
     * and then the BEEF data containing only the subject transaction and its dependencies.
     * This format ensures that the BEEF structure is atomic and contains no unrelated transactions.
     *
     * @param allowPartial If true, error will not be thrown if there are any missing sourceTransactions.
     *
     * @returns {number[]} - The serialized Atomic BEEF structure.
     * @throws Error if there are any missing sourceTransactions unless `allowPartial` is true.
     */
    toAtomicBEEFUint8Array(allowPartial) {
        this.materializeSourceTXIDs();
        const writer = new WriterUint8Array();
        const prefix = [1, 1, 1, 1];
        writer.write(prefix);
        const txHash = this.hash();
        writer.write(txHash);
        this.writeSerializedBEEF(writer, allowPartial);
        return writer.toUint8Array();
    }
    /**
     * Completes the transaction using a wallet interface, which will handle
     * signing and transaction finalization. This method converts the current
     * transaction into a format that can be processed by the wallet, and then
     * updates this transaction object with the result from the wallet.
     *
     * @param {WalletInterface} wallet - The BRC-100 compliant wallet to use for completing the transaction
     * @param {string} [actionDescription] - Optional description for the action
     * @param {string} [originator] - Optional originator domain name
     * @param {CreateActionOptions} [options] - Optional settings for transaction creation (e.g., acceptDelayedBroadcast, trustSelf, noSend, etc.)
     * @returns {Promise<void>}
     */
    async completeWithWallet(wallet, actionDescription, originator, options) {
        const inputCount = this.inputs.length;
        const outputCount = this.outputs.length;
        const description = actionDescription ?? `Transaction with ${inputCount} input(s) and ${outputCount} output(s)`;
        const hasTemplates = this.inputs.some(input => input.unlockingScriptTemplate != null);
        const actionArgs = await this.buildWalletActionArgs(description, hasTemplates);
        const atomicBEEF = hasTemplates
            ? await this.completeWalletTemplateAction(wallet, actionArgs, originator, options)
            : await this.completeWalletScriptAction(wallet, actionArgs, originator, options);
        // Create a new transaction from the atomic BEEF
        const newTransaction = Transaction.fromAtomicBEEF(atomicBEEF);
        // Update this transaction's properties with the new transaction's properties
        this.version = newTransaction.version;
        this.inputs = newTransaction.inputs;
        this.outputs = newTransaction.outputs;
        this.lockTime = newTransaction.lockTime;
        this.merklePath = newTransaction.merklePath;
        this.invalidateSerializationCaches();
        // Preserve metadata from the original transaction but update with any new metadata
        this.metadata = {
            ...this.metadata,
            ...newTransaction.metadata
        };
    }
    async buildWalletActionArgs(description, hasTemplates) {
        const actionArgs = {
            description,
            inputs: [],
            outputs: [],
            lockTime: this.lockTime,
            version: this.version
        };
        this.materializeSourceTXIDs();
        const beefData = new Beef();
        for (let index = 0; index < this.inputs.length; index++) {
            const input = this.inputs[index];
            if (input.sourceTransaction == null) {
                throw new Error('All inputs must have a sourceTransaction when using completeWithWallet');
            }
            beefData.mergeTransaction(input.sourceTransaction);
            actionArgs.inputs.push(await this.buildWalletInputArg(input, index, hasTemplates));
        }
        if (this.inputs.length > 0)
            actionArgs.inputBEEF = beefData.toUint8Array();
        actionArgs.outputs = this.outputs.map(output => ({
            satoshis: output.satoshis,
            lockingScript: output.lockingScript.toHex(),
            outputDescription: 'Output from source transaction'
        }));
        if (Array.isArray(this.metadata?.labels))
            actionArgs.labels = this.metadata.labels;
        return actionArgs;
    }
    async buildWalletInputArg(input, index, hasTemplates) {
        const inputArg = {
            outpoint: `${input.sourceTransaction.id('hex')}.${input.sourceOutputIndex}`,
            inputDescription: 'Input from source transaction',
            sequenceNumber: input.sequence
        };
        if (!hasTemplates) {
            if (input.unlockingScript == null) {
                throw new Error('All inputs must have an unlockingScript when using completeWithWallet');
            }
            inputArg.unlockingScript = input.unlockingScript.toHex();
            return inputArg;
        }
        if (input.unlockingScriptTemplate != null) {
            inputArg.unlockingScriptLength = await input.unlockingScriptTemplate.estimateLength(this, index);
        }
        else if (input.unlockingScript != null) {
            inputArg.unlockingScript = input.unlockingScript.toHex();
        }
        else {
            throw new Error(`Input ${index} must have either an unlockingScript or unlockingScriptTemplate`);
        }
        return inputArg;
    }
    async completeWalletTemplateAction(wallet, actionArgs, originator, options) {
        actionArgs.options = { ...options, signAndProcess: false };
        const { signableTransaction } = await wallet.createAction(actionArgs, originator);
        if (signableTransaction == null) {
            throw new Error('Wallet createAction did not return signableTransaction');
        }
        const partialTx = Transaction.fromBEEF(signableTransaction.tx);
        const spends = await this.buildWalletSpends(partialTx);
        const signActionOptions = options == null
            ? undefined
            : {
                acceptDelayedBroadcast: options.acceptDelayedBroadcast,
                returnTXIDOnly: options.returnTXIDOnly,
                noSend: options.noSend,
                sendWith: options.sendWith
            };
        const signResult = await wallet.signAction({
            reference: signableTransaction.reference,
            spends,
            options: signActionOptions
        }, originator);
        if (signResult.tx == null) {
            throw new Error('Wallet signAction did not return transaction data');
        }
        return signResult.tx;
    }
    async buildWalletSpends(partialTx) {
        const spends = {};
        for (let index = 0; index < this.inputs.length; index++) {
            const input = this.inputs[index];
            if (input.unlockingScriptTemplate != null) {
                const unlockingScript = await input.unlockingScriptTemplate.sign(partialTx, index);
                spends[index] = { unlockingScript: unlockingScript.toHex() };
            }
            else if (input.unlockingScript != null) {
                spends[index] = { unlockingScript: input.unlockingScript.toHex() };
            }
        }
        return spends;
    }
    async completeWalletScriptAction(wallet, actionArgs, originator, options) {
        if (options != null)
            actionArgs.options = options;
        const { tx } = await wallet.createAction(actionArgs, originator);
        if (tx == null) {
            throw new Error('Wallet createAction did not return transaction data');
        }
        return tx;
    }
    /**
     * Returns the formatted preimage of a transaction for the requested input index, signature scope (default SIGHASH_FORKID | SIGHASH_ALL), and optional subscript.
     * @param inputIndex - The index of the input to generate the preimage for
     * @param signatureScope - The signature scope to use for the preimage
     * @param subscript - The subscript to use for the preimage (optional)
     * @returns The formatted preimage
     */
    preimage(inputIndex, signatureScope, subscript) {
        inputIndex ??= 0;
        signatureScope ??= TransactionSignature.SIGHASH_FORKID | TransactionSignature.SIGHASH_ALL;
        if (inputIndex < 0 || inputIndex >= this.inputs.length) {
            throw new Error('Invalid input index');
        }
        const flags = signatureScope & 0xf0;
        if (flags !== 224 && flags !== 192 && flags !== 64) {
            throw new Error('FORKID must be set');
        }
        const coverage = signatureScope & 0x0f;
        if (coverage < 1 || coverage > 3) {
            throw new Error('Invalid signature coverage, must be all, none or single');
        }
        const input = this.inputs[inputIndex];
        if (input.sourceTransaction == null) {
            throw new Error('Source transaction is required');
        }
        const output = input.sourceTransaction.outputs[input.sourceOutputIndex];
        if (output == null) {
            throw new Error(`Source transaction's output at index ${input.sourceOutputIndex} is required`);
        }
        return TransactionSignature.format({
            sourceTXID: input.sourceTXID ?? input.sourceTransaction.id('hex'),
            sourceOutputIndex: input.sourceOutputIndex,
            sourceSatoshis: output.satoshis,
            transactionVersion: this.version,
            otherInputs: [],
            allInputs: this.inputs,
            inputIndex,
            outputs: this.outputs,
            inputSequence: input.sequence ?? 0xffffffff,
            subscript: subscript ?? output.lockingScript,
            lockTime: this.lockTime,
            scope: signatureScope
        });
    }
}
//# sourceMappingURL=Transaction.js.map